/**
 * CatalogFiltersPanel — panneau de filtres unifié (mobile + web).
 *
 *  - `mode="modal"`   : bottom-sheet plein écran sur mobile (et web < 768)
 *  - `mode="sidebar"` : colonne fixe à gauche sur web ≥ 768
 *
 * Sections collapsibles, swatches couleurs, slider prix, recherche marques,
 * filtres avancés masqués par défaut. Footer sticky avec "Réinitialiser" et
 * "Voir N produits". Compteurs grisés si 0.
 *
 * v1.12.345 — refonte filtres catalogue (Phase 2).
 */

import React, { useState, useMemo } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, TextInput, StyleSheet, Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useI18n } from '../i18n';
import { useTheme } from '../context/ThemeContext';
import AppModal from './AppModal';
import PartnerLogo from './PartnerLogo';
import {
  COLOR_PALETTE, CATEGORY_IDS, PRICE_RANGES, TIER_LABELS,
} from '../services/FiltersService';

// v1.12.357 — Theme-aware colors (light/dark via useTheme)
//   Mappe les couleurs sémantiques aux valeurs du ThemeContext.
//   Construit dynamiquement via buildColors(theme, dark).
function buildColors(theme, dark) {
  return {
    primary:      theme.accent,                 // doré (#9B7B5A light / #D4B896 dark)
    primaryFg:    '#FFFFFF',                    // texte sur fond accent
    border:       theme.bgBorder,
    borderDark:   dark ? '#666666' : '#9CA3AF',
    text:         theme.text,
    muted:        theme.textMuted,
    bg:           theme.bg,
    bgAlt:        theme.bgSubtle || theme.bgSurface,
    chipBg:       theme.bgSubtle || theme.bgRaised,
    chipBgActive: theme.btnPrimary,
    chipFgActive: theme.btnPrimaryText,
    disabled:     theme.textFaint || '#D1D5DB',
    danger:       '#DC2626',
  };
}

// ─── Sub-components (factories qui prennent `s` themé en paramètre) ─────────
// On retourne des composants qui closure sur `s` pour accéder aux styles
// themed sans avoir à passer `s` comme prop à chaque call site.
function makeSubcomponents(s) {
  const Section = ({ title, icon, defaultOpen = true, badge, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
      <View style={s.section}>
        <TouchableOpacity onPress={() => setOpen(o => !o)} style={s.sectionHeader} activeOpacity={0.7}>
          <View style={s.sectionTitleWrap}>
            {icon ? <Text style={s.sectionIcon}>{icon}</Text> : null}
            <Text style={s.sectionTitle}>{title}</Text>
            {badge ? <View style={s.badge}><Text style={s.badgeText}>{badge}</Text></View> : null}
          </View>
          <Text style={s.chevron}>{open ? '▾' : '▸'}</Text>
        </TouchableOpacity>
        {open ? <View style={s.sectionBody}>{children}</View> : null}
      </View>
    );
  };

  const CheckboxOption = ({ label, count, active, disabled, onPress }) => (
    <TouchableOpacity
      style={[s.checkRow, active && s.checkRowActive, disabled && s.checkRowDisabled]}
      onPress={disabled ? undefined : onPress}
      activeOpacity={0.7}
    >
      <View style={[s.checkbox, active && s.checkboxActive]}>
        {active ? <Text style={s.checkboxMark}>✓</Text> : null}
      </View>
      <Text style={[s.checkLabel, disabled && s.checkLabelDisabled]} numberOfLines={1}>{label}</Text>
      {typeof count === 'number' ? (
        <Text style={[s.checkCount, disabled && s.checkCountDisabled]}>{count}</Text>
      ) : null}
    </TouchableOpacity>
  );

  const ColorSwatch = ({ value, hex, label, count, active, disabled, onPress }) => {
    const isGradient = hex && hex.startsWith('linear-gradient');
    return (
      <TouchableOpacity
        style={[s.swatchWrap, disabled && s.swatchDisabled]}
        onPress={disabled ? undefined : onPress}
        activeOpacity={0.7}
      >
        <View style={[
          s.swatch,
          { backgroundColor: isGradient ? undefined : hex },
          Platform.OS === 'web' && isGradient ? { backgroundImage: hex } : null,
          active && s.swatchActive,
          hex === '#FFFFFF' && s.swatchWhiteBorder,
        ]}>
          {active ? <Text style={s.swatchMark}>✓</Text> : null}
        </View>
        <Text style={[s.swatchLabel, disabled && s.swatchLabelDisabled]}>{label}</Text>
        {typeof count === 'number' ? (
          <Text style={[s.swatchCount, disabled && s.swatchLabelDisabled]}>{count}</Text>
        ) : null}
      </TouchableOpacity>
    );
  };

  const Pill = ({ label, count, active, disabled, onPress }) => (
    <TouchableOpacity
      style={[s.pill, active && s.pillActive, disabled && s.pillDisabled]}
      onPress={disabled ? undefined : onPress}
      activeOpacity={0.7}
    >
      <Text style={[s.pillLabel, active && s.pillLabelActive, disabled && s.pillLabelDisabled]}>
        {label}
        {typeof count === 'number' ? <Text style={s.pillCount}>{' '}({count})</Text> : null}
      </Text>
    </TouchableOpacity>
  );

  return { Section, CheckboxOption, ColorSwatch, Pill };
}

// ─── Body principal ──────────────────────────────────────────────
function PanelBody({
  available, filters, onChange, locale,
  subfilterList, subfilterCounts, currentSubfilter, onSubfilterChange, labelForSubfilter,
  currentSort, onSortChange,
}) {
  const { t } = useI18n();
  const { dark, theme } = useTheme();
  // v1.12.357 — styles theme-aware (light/dark)
  const COLORS = useMemo(() => buildColors(theme, dark), [theme, dark]);
  const s = useMemo(() => makeStyles(COLORS), [COLORS]);
  const { Section, CheckboxOption, ColorSwatch, Pill } = useMemo(() => makeSubcomponents(s), [s]);
  const [brandQuery, setBrandQuery] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const isFR = locale === 'fr' || (locale || '').startsWith('fr');
  const L = (frTxt, enTxt) => (isFR ? frTxt : enTxt);

  // Labels catégorie (utilise translations.js si disponible)
  const CAT_LABEL_KEY = {
    'Robes': 'cat_dresses',     'Hauts': 'cat_tops',
    'Bas':   'cat_bottoms',     'Vestes': 'cat_jackets',
    'Manteaux': 'cat_coats',    'Chaussures': 'cat_shoes',
    'Sacs':  'cat_bags',        'Bijoux': 'cat_jewelry',
    'Accessoires': 'cat_accessories', 'Maillots': 'cat_swimwear',
  };
  const labelForCat = (id) => {
    try { const v = t(CAT_LABEL_KEY[id] || ''); if (v && v !== CAT_LABEL_KEY[id]) return v; } catch {}
    return id;
  };

  const colorLabels = {
    fr: { noir: 'Noir', blanc: 'Blanc', gris: 'Gris', beige: 'Beige', marron: 'Marron',
          marine: 'Marine', bleu: 'Bleu', rouge: 'Rouge', rose: 'Rose', vert: 'Vert',
          jaune: 'Jaune', orange: 'Orange', violet: 'Violet', doré: 'Doré', argenté: 'Argenté',
          multicolore: 'Multicolore' },
    en: { noir: 'Black', blanc: 'White', gris: 'Gray', beige: 'Beige', marron: 'Brown',
          marine: 'Navy', bleu: 'Blue', rouge: 'Red', rose: 'Pink', vert: 'Green',
          jaune: 'Yellow', orange: 'Orange', violet: 'Purple', doré: 'Gold', argenté: 'Silver',
          multicolore: 'Multicolor' },
  };
  const labelForColor = (v) => (colorLabels[isFR ? 'fr' : 'en'][v]) || v;

  const styleLabels = {
    fr: { chic: 'Chic', casual: 'Casual', smart_casual: 'Smart Casual', sportswear: 'Sport',
          bohemian: 'Bohème', romantic: 'Romantique', edgy: 'Edgy', vintage: 'Vintage',
          minimalist: 'Minimaliste', glamour: 'Glamour' },
    en: { chic: 'Chic', casual: 'Casual', smart_casual: 'Smart Casual', sportswear: 'Sportswear',
          bohemian: 'Bohemian', romantic: 'Romantic', edgy: 'Edgy', vintage: 'Vintage',
          minimalist: 'Minimalist', glamour: 'Glamour' },
  };
  const labelForStyle = (v) => (styleLabels[isFR ? 'fr' : 'en'][v]) || v;

  const materialLabels = {
    fr: { coton: 'Coton', soie: 'Soie', cuir: 'Cuir', laine: 'Laine', cachemire: 'Cachemire',
          denim: 'Denim', polyester: 'Polyester', lin: 'Lin', velours: 'Velours', satin: 'Satin' },
    en: { coton: 'Cotton', soie: 'Silk', cuir: 'Leather', laine: 'Wool', cachemire: 'Cashmere',
          denim: 'Denim', polyester: 'Polyester', lin: 'Linen', velours: 'Velvet', satin: 'Satin' },
  };
  const labelForMaterial = (v) => (materialLabels[isFR ? 'fr' : 'en'][v]) || v;

  const occasionLabels = {
    fr: { travail: 'Travail', brunch: 'Brunch', soirée: 'Soirée', sport: 'Sport',
          cérémonie: 'Cérémonie', casual: 'Casual', voyage: 'Voyage', date: 'Rendez-vous' },
    en: { travail: 'Work', brunch: 'Brunch', soirée: 'Evening', sport: 'Sport',
          cérémonie: 'Ceremony', casual: 'Casual', voyage: 'Travel', date: 'Date' },
  };
  const labelForOccasion = (v) => (occasionLabels[isFR ? 'fr' : 'en'][v]) || v;

  // ─── Toggle helpers ───
  const toggleInArray = (key, value) => {
    const arr = Array.isArray(filters[key]) ? filters[key] : [];
    const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
    const updated = { ...filters };
    if (next.length === 0) delete updated[key]; else updated[key] = next;
    onChange(updated);
  };

  const setPriceRange = (min, max) => {
    const updated = { ...filters };
    if (min === null && max === null) { delete updated.price_min; delete updated.price_max; }
    else { updated.price_min = min; if (max !== null) updated.price_max = max; else delete updated.price_max; }
    onChange(updated);
  };

  const selectedCats = filters.category || [];
  const selectedColors = filters.colors || [];
  const selectedBrands = filters.brands || [];
  const selectedSizes = filters.sizes || [];
  const selectedTiers = filters.partner_tier || [];
  const selectedPartners = filters.partners || [];
  const selectedStyles = filters.styles || [];
  const selectedMats = filters.materials || [];
  const selectedOccs = filters.occasions || [];

  // Marques filtrées par recherche
  const visibleBrands = useMemo(() => {
    const all = available.brands || [];
    const q = brandQuery.trim().toLowerCase();
    const list = q ? all.filter(b => b.value.toLowerCase().includes(q)) : all;
    return list.slice(0, 50);
  }, [available.brands, brandQuery]);

  const topBrands = useMemo(() => (available.brands || []).slice(0, 6), [available.brands]);

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ paddingBottom: 24 }}
      showsVerticalScrollIndicator
    >
      {/* TRI — visible si callback fournie */}
      {typeof onSortChange === 'function' ? (
        <Section title={L('Trier par', 'Sort by')} icon="↕" defaultOpen={false}>
          <View style={s.pillRow}>
            {[
              { key: 'relevance',  fr: 'Pertinence',     en: 'Relevance' },
              { key: 'price_asc',  fr: 'Prix ↑',         en: 'Price ↑' },
              { key: 'price_desc', fr: 'Prix ↓',         en: 'Price ↓' },
              { key: 'discount',   fr: 'Promo',          en: 'Discount' },
              { key: 'rating',     fr: 'Note',           en: 'Rating' },
            ].map(opt => {
              const active = (currentSort || 'relevance') === opt.key;
              return (
                <Pill
                  key={opt.key}
                  label={isFR ? opt.fr : opt.en}
                  active={active}
                  onPress={() => onSortChange(opt.key)}
                />
              );
            })}
          </View>
        </Section>
      ) : null}

      {/* CATÉGORIE */}
      <Section title={L('Catégorie', 'Category')} icon="🏷️" defaultOpen>
        <View style={s.grid2}>
          {(available.categories || []).map(opt => {
            const active = selectedCats.includes(opt.value);
            const disabled = opt.count === 0 && !active;
            return (
              <CheckboxOption
                key={opt.value}
                label={labelForCat(opt.value)}
                count={opt.count}
                active={active}
                disabled={disabled}
                onPress={() => toggleInArray('category', opt.value)}
              />
            );
          })}
        </View>
      </Section>

      {/* SOUS-CATÉGORIE — apparaît dynamiquement quand une catégorie est active */}
      {Array.isArray(subfilterList) && subfilterList.length > 1 && onSubfilterChange ? (
        <Section title={L('Type', 'Type')} icon="🏷" defaultOpen>
          <View style={s.pillRow}>
            {subfilterList.map(sub => {
              const count = subfilterCounts?.[sub];
              const active = currentSubfilter === sub;
              const disabled = count === 0 && !active;
              const label = typeof labelForSubfilter === 'function' ? labelForSubfilter(sub) : sub;
              return (
                <Pill
                  key={sub}
                  label={label}
                  count={typeof count === 'number' ? count : undefined}
                  active={active}
                  disabled={disabled}
                  onPress={() => onSubfilterChange(sub)}
                />
              );
            })}
          </View>
        </Section>
      ) : null}

      {/* PARTENAIRE (multi-select avec logos) */}
      {(available.partners || []).length > 0 && (
        <Section title={L('Partenaires', 'Partners')} icon="🤝" defaultOpen>
          {available.partners.map(opt => {
            const active = selectedPartners.includes(opt.value);
            const disabled = opt.count === 0 && !active;
            return (
              <TouchableOpacity
                key={opt.value}
                style={[s.partnerRow, active && s.partnerRowActive, disabled && s.checkRowDisabled]}
                onPress={disabled ? undefined : () => toggleInArray('partners', opt.value)}
                activeOpacity={0.7}
              >
                <View style={[s.checkbox, active && s.checkboxActive]}>
                  {active ? <Text style={s.checkboxMark}>✓</Text> : null}
                </View>
                <PartnerLogo partner={opt.value} size={20} />
                <Text style={[s.checkLabel, disabled && s.checkLabelDisabled]} numberOfLines={1}>
                  {opt.label}
                </Text>
                <Text style={[s.checkCount, disabled && s.checkCountDisabled]}>{opt.count}</Text>
              </TouchableOpacity>
            );
          })}
        </Section>
      )}

      {/* COULEUR */}
      <Section title={L('Couleur', 'Color')} icon="🎨" defaultOpen>
        <View style={s.swatchGrid}>
          {(available.colors || []).map(opt => {
            const active = selectedColors.includes(opt.value);
            const disabled = opt.count === 0 && !active;
            return (
              <ColorSwatch
                key={opt.value}
                value={opt.value}
                hex={opt.hex}
                label={labelForColor(opt.value)}
                count={opt.count}
                active={active}
                disabled={disabled}
                onPress={() => toggleInArray('colors', opt.value)}
              />
            );
          })}
        </View>
      </Section>

      {/* PRIX */}
      <Section title={L('Prix', 'Price')} icon="💰" defaultOpen>
        {/* 2 inputs Min/Max — précision libre */}
        <View style={s.priceInputsRow}>
          <View style={s.priceInputWrap}>
            <Text style={s.priceInputLabel}>{L('Min', 'Min')}</Text>
            <TextInput
              style={s.priceInput}
              value={typeof filters.price_min === 'number' ? String(filters.price_min) : ''}
              onChangeText={(txt) => {
                const n = parseInt(txt.replace(/[^0-9]/g, ''), 10);
                const updated = { ...filters };
                if (isNaN(n)) delete updated.price_min;
                else updated.price_min = n;
                onChange(updated);
              }}
              keyboardType="numeric"
              placeholder="0"
              placeholderTextColor={COLORS.muted}
              accessibilityLabel={L('Prix minimum en euros', 'Minimum price in euros')}
            />
            <Text style={s.priceCurrency}>€</Text>
          </View>
          <Text style={s.priceDash}>—</Text>
          <View style={s.priceInputWrap}>
            <Text style={s.priceInputLabel}>{L('Max', 'Max')}</Text>
            <TextInput
              style={s.priceInput}
              value={typeof filters.price_max === 'number' ? String(filters.price_max) : ''}
              onChangeText={(txt) => {
                const n = parseInt(txt.replace(/[^0-9]/g, ''), 10);
                const updated = { ...filters };
                if (isNaN(n)) delete updated.price_max;
                else updated.price_max = n;
                onChange(updated);
              }}
              keyboardType="numeric"
              placeholder={available.price?.max ? String(Math.ceil(available.price.max)) : '1000'}
              placeholderTextColor={COLORS.muted}
              accessibilityLabel={L('Prix maximum en euros', 'Maximum price in euros')}
            />
            <Text style={s.priceCurrency}>€</Text>
          </View>
        </View>

        {/* Tranches rapides */}
        <Text style={s.subHeader}>{L('Tranches rapides', 'Quick ranges')}</Text>
        <View style={s.pillRow}>
          {(available.price?.ranges || PRICE_RANGES).map(r => {
            const active = filters.price_min === r.min && filters.price_max === r.max;
            const disabled = (r.count === 0) && !active;
            const label = r.max === null
              ? L(`${r.min}€+`, `€${r.min}+`)
              : (r.min === 0
                  ? L(`< ${r.max}€`, `< €${r.max}`)
                  : L(`${r.min}-${r.max}€`, `€${r.min}-${r.max}`));
            return (
              <Pill
                key={r.value}
                label={label}
                count={r.count}
                active={active}
                disabled={disabled}
                onPress={() => active ? setPriceRange(null, null) : setPriceRange(r.min, r.max)}
              />
            );
          })}
        </View>
        {(typeof filters.price_min === 'number' || typeof filters.price_max === 'number') ? (
          <TouchableOpacity onPress={() => setPriceRange(null, null)} style={s.clearMini}>
            <Text style={s.clearMiniText}>{L('Effacer prix', 'Clear price')}</Text>
          </TouchableOpacity>
        ) : null}
      </Section>

      {/* MARQUE */}
      <Section title={L('Marque', 'Brand')} icon="🏷️" defaultOpen={false}>
        <TextInput
          placeholder={L('Rechercher une marque…', 'Search brand…')}
          placeholderTextColor={COLORS.muted}
          value={brandQuery}
          onChangeText={setBrandQuery}
          style={s.searchInput}
        />
        {topBrands.length > 0 && brandQuery === '' ? (
          <>
            <Text style={s.subHeader}>⭐ {L('Populaires', 'Popular')}</Text>
            <View>
              {topBrands.map(b => {
                const active = selectedBrands.includes(b.value);
                return (
                  <CheckboxOption
                    key={`top-${b.value}`}
                    label={b.value}
                    count={b.count}
                    active={active}
                    onPress={() => toggleInArray('brands', b.value)}
                  />
                );
              })}
            </View>
            <Text style={s.subHeader}>{L('Toutes les marques', 'All brands')}</Text>
          </>
        ) : null}
        <View>
          {visibleBrands.map(b => {
            const active = selectedBrands.includes(b.value);
            return (
              <CheckboxOption
                key={`all-${b.value}`}
                label={b.value}
                count={b.count}
                active={active}
                onPress={() => toggleInArray('brands', b.value)}
              />
            );
          })}
          {visibleBrands.length === 0 && brandQuery !== '' ? (
            <Text style={s.emptyHint}>{L('Aucune marque trouvée', 'No brand found')}</Text>
          ) : null}
        </View>
      </Section>

      {/* TAILLE */}
      {(available.sizes || []).length > 0 ? (
        <Section title={L('Taille', 'Size')} icon="📏" defaultOpen={false}>
          <View style={s.pillRow}>
            {(available.sizes || []).map(opt => {
              const active = selectedSizes.includes(opt.value);
              const disabled = opt.count === 0 && !active;
              return (
                <Pill
                  key={opt.value}
                  label={opt.value}
                  count={opt.count}
                  active={active}
                  disabled={disabled}
                  onPress={() => toggleInArray('sizes', opt.value)}
                />
              );
            })}
          </View>
        </Section>
      ) : null}

      {/* PARTENAIRE */}
      <Section title={L('Partenaire', 'Partner')} icon="🤝" defaultOpen={false}>
        {(available.partnerTiers || []).map(opt => {
          const active = selectedTiers.includes(opt.value);
          const disabled = opt.count === 0 && !active;
          return (
            <TouchableOpacity
              key={opt.value}
              style={[s.tierRow, active && s.tierRowActive, disabled && s.checkRowDisabled]}
              onPress={disabled ? undefined : () => {
                // Single select (radio)
                const updated = { ...filters };
                if (active) delete updated.partner_tier;
                else updated.partner_tier = [opt.value];
                onChange(updated);
              }}
              activeOpacity={0.7}
            >
              <View style={s.tierMain}>
                <Text style={[s.tierTitle, active && s.tierTitleActive]}>
                  {isFR ? opt.fr : opt.en}
                </Text>
                {(isFR ? opt.badge_fr : opt.badge_en) ? (
                  <Text style={s.tierBadge}>{isFR ? opt.badge_fr : opt.badge_en}</Text>
                ) : null}
                <Text style={s.tierDesc}>{L(`Jusqu'à ${opt.maxReward}/100€`, `Up to ${opt.maxReward}/€100`)}</Text>
              </View>
              <Text style={s.tierCount}>{opt.count}</Text>
            </TouchableOpacity>
          );
        })}
      </Section>

      {/* + FILTRES AVANCÉS */}
      <TouchableOpacity onPress={() => setShowAdvanced(v => !v)} style={s.advToggle} activeOpacity={0.7}>
        <Text style={s.advToggleText}>
          {showAdvanced
            ? L('− Masquer les filtres avancés', '− Hide advanced filters')
            : L('+ Filtres avancés (style, matière, occasion)', '+ Advanced filters (style, material, occasion)')}
        </Text>
      </TouchableOpacity>

      {showAdvanced ? (
        <>
          {(available.styles || []).length > 0 ? (
            <Section title={L('Style', 'Style')} icon="✨" defaultOpen={false}>
              {available.styles.map(opt => (
                <CheckboxOption
                  key={opt.value}
                  label={labelForStyle(opt.value)}
                  count={opt.count}
                  active={selectedStyles.includes(opt.value)}
                  onPress={() => toggleInArray('styles', opt.value)}
                />
              ))}
            </Section>
          ) : null}
          {(available.materials || []).length > 0 ? (
            <Section title={L('Matière', 'Material')} icon="🧶" defaultOpen={false}>
              {available.materials.map(opt => (
                <CheckboxOption
                  key={opt.value}
                  label={labelForMaterial(opt.value)}
                  count={opt.count}
                  active={selectedMats.includes(opt.value)}
                  onPress={() => toggleInArray('materials', opt.value)}
                />
              ))}
            </Section>
          ) : null}
          {(available.occasions || []).length > 0 ? (
            <Section title={L('Occasion', 'Occasion')} icon="🎯" defaultOpen={false}>
              {available.occasions.map(opt => (
                <CheckboxOption
                  key={opt.value}
                  label={labelForOccasion(opt.value)}
                  count={opt.count}
                  active={selectedOccs.includes(opt.value)}
                  onPress={() => toggleInArray('occasions', opt.value)}
                />
              ))}
            </Section>
          ) : null}
        </>
      ) : null}
    </ScrollView>
  );
}

// ─── Composant principal ─────────────────────────────────────────
export default function CatalogFiltersPanel({
  mode = 'modal',            // 'modal' | 'sidebar'
  visible = false,           // modal only
  onClose,                   // modal only
  filters = {},
  available,
  onChange,
  onReset,
  onApply,
  resultsCount,
  locale,
  // v1.12.350 — Sous-catégorie dynamique : props pour réutiliser la logique
  //   matchesSubfilter / SUBFILTERS du CatalogScreen sans dupliquer.
  subfilterList,             // string[] | null — liste des sous-filtres pour la catégorie courante
  subfilterCounts,           // { [sub]: number } — compteur par sous-filtre
  currentSubfilter,          // string — sous-filtre actif ('Tout' ou autre)
  onSubfilterChange,         // (sub: string) => void
  labelForSubfilter,         // (sub: string) => string — traduction du label
  // v1.12.352 — Tri (rendu accessible depuis le panel/sidebar)
  currentSort,               // string — 'relevance' | 'price_asc' | 'price_desc' | 'discount' | 'rating'
  onSortChange,              // (sortKey: string) => void
}) {
  const { t } = useI18n();
  const { dark, theme } = useTheme();
  const insets = useSafeAreaInsets();
  // v1.12.357 — styles theme-aware (light/dark)
  const COLORS = useMemo(() => buildColors(theme, dark), [theme, dark]);
  const s = useMemo(() => makeStyles(COLORS), [COLORS]);
  const isFR = (locale || '').startsWith('fr');
  const L = (frTxt, enTxt) => (isFR ? frTxt : enTxt);

  const Header = (
    <View style={s.header}>
      <Text style={s.headerTitle} accessibilityRole="header">{L('Filtres', 'Filters')}</Text>
      <View style={s.headerRight}>
        <TouchableOpacity
          onPress={onReset}
          style={s.headerReset}
          accessibilityRole="button"
          accessibilityLabel={L('Réinitialiser les filtres', 'Reset filters')}
        >
          <Text style={s.headerResetText}>{L('Réinitialiser', 'Reset')}</Text>
        </TouchableOpacity>
        {mode === 'modal' ? (
          <TouchableOpacity
            onPress={onClose}
            style={s.headerClose}
            hitSlop={{ top:10, right:10, bottom:10, left:10 }}
            accessibilityRole="button"
            accessibilityLabel={L('Fermer les filtres', 'Close filters')}
          >
            <Text style={s.headerCloseText}>×</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );

  // Padding bottom = max(12, safe area inset bottom) pour respecter le home indicator iPhone
  //   et ne pas être caché sous la barre Android. Sidebar mode utilise padding constant.
  const footerPadBottom = mode === 'sidebar' ? 12 : Math.max(12, insets.bottom);
  // v1.12.358 — Padding top sur mobile : le modal bottom-sheet ne doit pas
  //   commencer trop haut (status bar / notch / address bar) — laisse 8-12% du viewport
  //   au-dessus pour signal visuel "tiroir qui monte".
  const modalTopOffset = Platform.OS === 'web'
    ? null // déjà géré via height:'85vh' + justifyContent:flex-end
    : (insets.top + 60); // iOS/Android : on garde 60px + safe area au-dessus du modal
  const Footer = (
    <View style={[s.footer, { paddingBottom: footerPadBottom }]}>
      <TouchableOpacity
        onPress={onApply || onClose}
        style={s.applyBtn}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={L('Voir les produits filtrés', 'Show filtered products')}
      >
        <Text style={s.applyBtnText}>
          {typeof resultsCount === 'number'
            ? L(`Voir les ${resultsCount} produits`, `Show ${resultsCount} products`)
            : L('Voir les produits', 'Show products')}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const Body = (
    <PanelBody
      available={available || {}}
      filters={filters}
      onChange={onChange}
      locale={locale}
      subfilterList={subfilterList}
      subfilterCounts={subfilterCounts}
      currentSubfilter={currentSubfilter}
      onSubfilterChange={onSubfilterChange}
      labelForSubfilter={labelForSubfilter}
      currentSort={currentSort}
      onSortChange={onSortChange}
    />
  );

  // Sidebar mode (web ≥ 768) : rendu inline, toujours visible
  if (mode === 'sidebar') {
    return (
      <View style={s.sidebar}>
        {Header}
        {Body}
        {Footer}
      </View>
    );
  }

  // Modal mode : bottom-sheet ancré au bas (mobile ET web) — coins arrondis en haut
  return (
    <AppModal
      visible={visible}
      onRequestClose={onClose}
      animationType="slide"
      webMaxWidth={560}
      webBackdropClosable={true}
      webScrollable={false}
      containerStyle={s.modalOverlay}
      backdropStyle={s.modalBackdrop}
    >
      <View style={[s.modalInner, modalTopOffset != null ? { marginTop: modalTopOffset } : null]}>
        {/* Drag handle iOS-style : indique visuellement que c'est un bottom-sheet */}
        <View style={s.dragHandleWrap}>
          <View style={s.dragHandle} />
        </View>
        {Header}
        {Body}
        {Footer}
      </View>
    </AppModal>
  );
}

// ─── Styles (theme-aware, construits via makeStyles dans le composant) ─────
function makeStyles(COLORS) {
  return StyleSheet.create({
  // ─── Sidebar (web) ───
  sidebar: {
    width: 320,
    backgroundColor: COLORS.bg,
    borderRightWidth: 1,
    borderRightColor: COLORS.border,
    height: '100%',
    flexShrink: 0,
  },

  // ─── Modal bottom-sheet (mobile + web) ───
  // L'overlay couvre le viewport entier ; le contenu est ancré en bas.
  modalOverlay: {
    // surcharge de AppModal webOverlay
    ...(Platform.OS === 'web' ? { justifyContent: 'flex-end', alignItems: 'center' } : {}),
  },
  modalBackdrop: {
    // backdrop assombri pour signaler le mode modal (sinon le contenu derrière distrait)
    ...(Platform.OS === 'web' ? { backgroundColor: 'rgba(0,0,0,0.35)' } : {}),
  },
  modalInner: {
    backgroundColor: COLORS.bg,
    width: '100%',
    // Bottom-sheet : ancré en bas, coins arrondis en haut, ne couvre pas tout l'écran
    ...(Platform.OS === 'web'
      ? {
          maxWidth: 560,
          height: '85vh',
          maxHeight: 760,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 -8px 32px rgba(0,0,0,0.18)',
        }
      : {
          minHeight: 400,
          maxHeight: '90%',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        }),
  },

  // ─── Drag handle iOS-style (visual cue de bottom-sheet) ───
  dragHandleWrap: {
    alignItems: 'center',
    paddingTop: 8,
    paddingBottom: 4,
  },
  dragHandle: {
    width: 36,
    height: 5,
    borderRadius: 3,
    backgroundColor: 'rgba(0,0,0,0.15)',
  },

  // ─── Header ───
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    backgroundColor: COLORS.bg,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: COLORS.text,
  },
  headerRight: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  // v1.12.349 — touch targets ≥ 48dp pour Apple HIG + Google Material
  headerReset: { paddingHorizontal: 12, paddingVertical: 12, minHeight: 48, justifyContent: 'center' },
  headerResetText: { color: COLORS.primary, fontWeight: '600', fontSize: 14 },
  headerClose: { paddingHorizontal: 12, paddingVertical: 8, minWidth: 48, minHeight: 48, alignItems: 'center', justifyContent: 'center' },
  headerCloseText: { fontSize: 28, color: COLORS.text, lineHeight: 30 },

  // ─── Section ───
  section: {
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitleWrap: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  sectionIcon: { fontSize: 16 },
  sectionTitle: { fontSize: 15, fontWeight: '600', color: COLORS.text },
  badge: {
    backgroundColor: COLORS.primary,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginLeft: 4,
  },
  badgeText: { color: COLORS.primaryFg, fontSize: 11, fontWeight: '700' },
  chevron: { fontSize: 14, color: COLORS.muted, width: 16, textAlign: 'right' },
  sectionBody: { marginTop: 12 },

  // ─── Grid 2 colonnes (catégories) ───
  grid2: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 6,
    columnGap: 6,
  },

  // ─── Checkbox row ───
  // v1.12.349 — minHeight 48 : Apple HIG = 44pt, Google Material = 48dp.
  //   On prend le plus haut pour passer les 2 store reviews.
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 6,
    borderRadius: 8,
    gap: 10,
    minWidth: '48%',
    minHeight: 48,
  },
  checkRowActive: { backgroundColor: COLORS.bgAlt },
  checkRowDisabled: { opacity: 0.4 },
  checkbox: {
    width: 20, height: 20,
    borderWidth: 1.5,
    borderColor: COLORS.borderDark,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxActive: { backgroundColor: COLORS.primary, borderColor: COLORS.primary },
  checkboxMark: { color: COLORS.primaryFg, fontSize: 13, fontWeight: '700', lineHeight: 14 },
  checkLabel: { color: COLORS.text, fontSize: 14, flex: 1 },
  checkLabelDisabled: { color: COLORS.disabled },
  checkCount: { color: COLORS.muted, fontSize: 13 },
  checkCountDisabled: { color: COLORS.disabled },

  // ─── Swatch ───
  swatchGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 14,
    columnGap: 8,
  },
  swatchWrap: { alignItems: 'center', width: 64 },
  swatch: {
    width: 38, height: 38, borderRadius: 19,
    borderWidth: 1, borderColor: COLORS.border,
    alignItems: 'center', justifyContent: 'center',
  },
  swatchWhiteBorder: { borderColor: COLORS.borderDark },
  swatchActive: { borderWidth: 3, borderColor: COLORS.primary },
  swatchDisabled: { opacity: 0.35 },
  swatchMark: { color: '#FFFFFF', fontSize: 14, fontWeight: '800', textShadowColor: '#000', textShadowRadius: 2 },
  swatchLabel: { fontSize: 11, color: COLORS.text, marginTop: 4, textAlign: 'center' },
  swatchCount: { fontSize: 10, color: COLORS.muted },
  swatchLabelDisabled: { color: COLORS.disabled },

  // ─── Pill (tranches prix, tailles) ───
  // v1.12.349 — minHeight 48 + paddingVertical 13 pour respecter Apple HIG 44pt
  //   et Google Material 48dp simultanément
  pillRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  pill: {
    paddingHorizontal: 16,
    paddingVertical: 13,
    borderRadius: 24,
    minHeight: 48,
    justifyContent: 'center',
    backgroundColor: COLORS.chipBg,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  pillActive: { backgroundColor: COLORS.chipBgActive, borderColor: COLORS.chipBgActive },
  pillDisabled: { opacity: 0.4 },
  pillLabel: { color: COLORS.text, fontSize: 13, fontWeight: '500' },
  pillLabelActive: { color: COLORS.chipFgActive, fontWeight: '600' },
  pillLabelDisabled: { color: COLORS.disabled },
  pillCount: { color: COLORS.muted, fontWeight: '400' },

  // ─── Inputs Min/Max prix ───
  priceInputsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 14,
  },
  priceInputWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: Platform.OS === 'web' ? 8 : 10,
    backgroundColor: COLORS.bg,
    minHeight: 48,
  },
  priceInputLabel: {
    color: COLORS.muted,
    fontSize: 11,
    marginRight: 6,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  priceInput: {
    flex: 1,
    fontSize: 14,
    color: COLORS.text,
    fontWeight: '600',
    paddingHorizontal: 0,
    paddingVertical: 0,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  priceCurrency: {
    color: COLORS.muted,
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 4,
  },
  priceDash: {
    color: COLORS.muted,
    fontSize: 16,
    fontWeight: '600',
  },

  // ─── Search input (marques) ───
  searchInput: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'web' ? 8 : 10,
    fontSize: 14,
    color: COLORS.text,
    marginBottom: 10,
    backgroundColor: COLORS.bg,
  },
  subHeader: { fontSize: 12, color: COLORS.muted, marginTop: 8, marginBottom: 4, fontWeight: '600' },
  emptyHint: { color: COLORS.muted, fontSize: 13, paddingVertical: 12, textAlign: 'center' },
  clearMini: { alignSelf: 'flex-start', paddingVertical: 6 },
  clearMiniText: { color: COLORS.primary, fontSize: 13, fontWeight: '600' },

  // ─── Partner row (multi-select avec logo) ───
  partnerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderRadius: 8,
    gap: 10,
  },
  partnerRowActive: { backgroundColor: COLORS.bgAlt },

  // ─── Tier row ───
  tierRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: COLORS.border,
    marginBottom: 8,
  },
  tierRowActive: { borderColor: COLORS.primary, backgroundColor: COLORS.bgAlt },
  tierMain: { flex: 1 },
  tierTitle: { fontSize: 15, fontWeight: '700', color: COLORS.text },
  tierTitleActive: { color: COLORS.primary },
  tierBadge: { fontSize: 12, color: COLORS.muted, marginTop: 2 },
  tierDesc: { fontSize: 11, color: COLORS.muted, marginTop: 2 },
  tierCount: { fontSize: 14, color: COLORS.muted, fontWeight: '600' },

  // ─── Advanced toggle ───
  advToggle: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    alignItems: 'center',
  },
  advToggleText: { color: COLORS.primary, fontWeight: '600', fontSize: 14 },

  // ─── Footer ───
  footer: {
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    backgroundColor: COLORS.bg,
  },
  applyBtn: {
    backgroundColor: COLORS.chipBgActive,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52, // Apple + Google : bouton CTA principal largement ≥ 48
  },
  applyBtnText: { color: COLORS.chipFgActive, fontWeight: '700', fontSize: 15 },
  });
}
